import {
  PRE_CHART_LAB_TESTS_MAX,
  type PreChartLabMode,
  type PreChartLabs,
  preChartLabsSchema,
} from "@rosc/contracts";
import { useSyncExternalStore } from "react";

export type { PreChartLabMode, PreChartLabs };

// "Pre-chart labs" (clinician feedback 03/11): which lab values a pre-chart
// takes from documents. The clinician's setting applies to every pre-chart.
// It is remembered in this browser,
// per account, like the template favourites (the account has no server-side
// field for it yet; that would need a database migration). The choice is
// sent with each assistant edit and only used on a pre-chart draft, and is
// where the lab report hint's choice starts.

export const PRE_CHART_LABS_DEFAULT: PreChartLabs = { mode: "all" };

type Account = string | null | undefined;
const ACCOUNT_PREFIX = "rosc:preChartLabs:";

const get = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const put = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page.
  }
};

const listeners = new Set<() => void>();
let version = 0;
const emit = (): void => {
  version++;
  for (const listener of listeners) listener();
};
const onStorage = (event: StorageEvent): void => {
  if (event.key === null || event.key.startsWith(ACCOUNT_PREFIX)) emit();
};
const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
};
// Re-renders when a setting changes (here or in another tab).
export const usePreChartLabsVersion = (): number =>
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );

const parse = (raw: string | null): PreChartLabs | null => {
  if (!raw) return null;
  try {
    const parsed = preChartLabsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const accountKey = (account: Account): string =>
  `${ACCOUNT_PREFIX}${account ?? "local"}`;

// The test list as the clinician types it: one name per comma, semicolon
// or line (the same rule as the server's parseLabTestList).
export const parseTestList = (text: string): string[] => {
  const seen = new Set<string>();
  const tests: string[] = [];
  for (const part of text.split(/[,;\n]+/)) {
    const name = part.trim().replace(/\s+/g, " ").slice(0, 60);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    tests.push(name);
  }
  return tests.slice(0, PRE_CHART_LAB_TESTS_MAX);
};

// A setting as saved: "Only these tests" without a test is "All values".
export const normalizeLabs = (value: PreChartLabs): PreChartLabs =>
  value.mode === "tests"
    ? value.tests?.length
      ? { mode: "tests", tests: [...value.tests] }
      : PRE_CHART_LABS_DEFAULT
    : { mode: value.mode };

export const readAccountLabs = (account: Account): PreChartLabs =>
  parse(get(accountKey(account))) ?? PRE_CHART_LABS_DEFAULT;

export const writeAccountLabs = (
  account: Account,
  value: PreChartLabs,
): void => {
  const next = normalizeLabs(value);
  put(accountKey(account), next.mode === "all" ? null : JSON.stringify(next));
  emit();
};

// What an edit of a pre-chart sends: the clinician's setting; nothing for
// "All values". A lab report added from the assistant's hint sends its own
// choice instead (PreChartLabsHint).
export const preChartLabsFor = (account: Account): PreChartLabs | undefined => {
  const value = readAccountLabs(account);
  return value.mode === "all" ? undefined : value;
};

// One test of an attached lab report, for "Custom": its name as printed and
// its value with the unit, to tick.
export interface ReportLabTest {
  name: string;
  value: string;
}

// Lines that are not results ("Collected: 2026-10-28 08:10", the table's
// header).
const NOT_A_RESULT =
  /^(?:collected|reported|received|printed|ordered|verified|date|time|page|phone|tel|fax|dob|date of birth|age|mrn|hcn|patient|specimen|accession|requisition|order|lab(?:oratory)?|test|result|units?|flag)\b/i;
const UNIT = /^(?:%|[A-Za-zµμ][A-Za-z0-9µμ%/.^·×*-]*)$/;

// The tests of a lab report's text, one per line as the reader ("Hemoglobin
// A1c | 7.6 | H | 4.0-5.6 | %") or the file's text layer ("Hemoglobin A1c
// 7.6 H 4.0-5.6 %") writes them. The server reads the same lines
// (reportLabResults); a picked name is matched there by name.
export const reportLabTests = (
  text: string | null | undefined,
): ReportLabTest[] => {
  const seen = new Set<string>();
  const tests: ReportLabTest[] = [];
  for (const raw of (text ?? "").split("\n")) {
    const line = raw
      .replace(/\s*\|\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!line || NOT_A_RESULT.test(line)) continue;
    if (/\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}:\d{2}\b/.test(line)) continue;
    const match =
      /^([A-Za-z][A-Za-z0-9 ()/'+,.-]*?[A-Za-z0-9)])\s*[:=]?\s+([<>≤≥]?\s*\d+(?:[.,]\d+)?)(?:\s+(.*))?$/.exec(
        line,
      );
    if (!match) continue;
    const name = (match[1] ?? "").trim().slice(0, 60);
    if (!/[A-Za-z]{2}/.test(name)) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    // The unit is the last word when it is one ("4.0-5.6 %" → "%").
    const last = (match[3] ?? "").split(" ").at(-1) ?? "";
    const unit =
      last && UNIT.test(last) && !/^(?:H|L|HH|LL|A|C)$/.test(last) ? last : "";
    const value = (match[2] ?? "").replace(/\s+/g, "");
    tests.push({
      name,
      value: unit ? (unit === "%" ? `${value}%` : `${value} ${unit}`) : value,
    });
    if (tests.length >= PRE_CHART_LAB_TESTS_MAX) break;
  }
  return tests;
};

// Where a lab report's values go by default (clinical lead, 05/11/2026):
// Investigations in a specialist note, Objective in a SOAP note, else the
// note's results heading; the first heading otherwise.
export const labsDefaultHeading = (
  headings: readonly string[],
): string | null =>
  headings.find((heading) => /investigation/i.test(heading)) ??
  headings.find((heading) => /objective/i.test(heading)) ??
  headings.find(
    (heading) =>
      /\bresults?\b|\blabs?\b|laborator|diagnostic/i.test(heading) &&
      !/plan|assessment|impression/i.test(heading),
  ) ??
  headings[0] ??
  null;

// The tests of the report the clinician's "Only these tests" setting names
// (picked to start with in "Custom").
export const settingPicks = (
  setting: PreChartLabs,
  tests: readonly ReportLabTest[],
): string[] => {
  if (setting.mode !== "tests") return tests.map((test) => test.name);
  const names = (setting.tests ?? []).map((name) =>
    name.toLowerCase().replace(/[\s.\-_()]/g, ""),
  );
  return tests
    .filter((test) => {
      const key = test.name.toLowerCase().replace(/[\s.\-_()]/g, "");
      return names.some((name) => key === name || key.includes(name));
    })
    .map((test) => test.name);
};
